/**
 * Shared PDF generator base for strategy proformas (pdfmake doc definitions).
 * Strategy subclasses keep page-specific content (lower-right / page 2 / CMA body).
 */

import {
  handleImageUrl,
  formatPhoneNumber,
  limitProformaGalleryImages,
} from '../utils/pdfUtils'
import { currencyFormat } from '@/utils/number'
import {
  startCase,
  get,
  map,
  compact,
  chunk,
  unzip,
} from '@/utils/lodash'

export class BasePDFGenerator {
  constructor(
    property,
    state,
    closeSnackbar,
    urlAffiliate,
    affiliate,
    urlDomain,
    session,
    comps,
  ) {
    this.property = property
    this.state = state
    this.closeSnackbar = closeSnackbar
    this.urlAffiliate = urlAffiliate
    this.affiliate = affiliate
    this.urlDomain = urlDomain
    this.session = session
    this.comps = comps
  }

  /**
   * Hero price shown on page 1 image overlay. Override per strategy when needed.
   */
  getPage1HeroPrice() {
    const { acquisition = {} } = this.property
    const analysis = acquisition.analysis || {}
    return analysis.offerPrice ? analysis.offerPrice : analysis.listPrice
  }

  buildDocTitle(suffix = 'finance') {
    const { property } = this
    return `${property.address}-${startCase(property.city)}-${startCase(
      property.state,
    )}-${property.zip}-${suffix}`
  }

  async generateSinglePageDocDefinition() {
    const { property } = this
    const { proformaImagePage = true } = property

    const page1Top = await this.generatePage1Top()
    const page1LowerLeft = await this.generatePage1LowerLeft()
    const page1LowerRight = await this.generatePage1LowerRight()
    const imagePages = await this.generateImagePages()
    const footerContent = await this.generateFooter()

    return {
      pageSize: 'A4',
      pageMargins: [0, 0, 0, 0],
      title: this.buildDocTitle(),
      content: [
        page1Top,
        {
          columns: [
            page1LowerLeft,
            {
              width: '*',
              stack: Array.isArray(page1LowerRight)
                ? page1LowerRight
                : [page1LowerRight],
            },
          ],
        },
        // Flowing page-1 footer (pdfmake footer callback needs bottom margin,
        // which steals space from later pages — keep footer in content instead).
        footerContent,
        ...(proformaImagePage ? imagePages : []),
      ],
      defaultStyle: {
        fontSize: 8,
      },
    }
  }

  async generateTwoPageDocDefinition() {
    const { property } = this
    const { proformaImagePage = true } = property

    const page1Top = await this.generatePage1Top()
    const page1LowerLeft = await this.generatePage1LowerLeft()
    const page1LowerRight = await this.generatePage1LowerRight()
    const page2Top = await this.generatePage2Top()
    const page2Bottom = await this.generatePage2Bottom()
    const imagePages = await this.generateImagePages()
    const footerContent = await this.generateFooter()

    return {
      pageSize: 'A4',
      pageMargins: [0, 0, 0, 0],
      title: this.buildDocTitle(),
      content: [
        page1Top,
        {
          columns: [
            page1LowerLeft,
            {
              width: '*',
              stack: Array.isArray(page1LowerRight)
                ? page1LowerRight
                : [page1LowerRight],
            },
          ],
        },
        // Flowing page-1 footer (pdfmake footer callback needs bottom margin,
        // which steals space from later pages — keep footer in content instead).
        footerContent,
        {
          pageBreak: 'before',
          columns: page2Top,
        },
        {
          stack: Array.isArray(page2Bottom) ? page2Bottom : [page2Bottom],
        },
        ...(proformaImagePage ? imagePages : []),
      ],
      defaultStyle: {
        fontSize: 8,
      },
    }
  }

  async generatePage1Top() {
    const { property } = this
    const { images = [] } = property

    const propertyImage =
      images.length > 0
        ? await handleImageUrl(images[0].url)
        : await handleImageUrl('/images/default_image.png')

    const offerPrice = this.getPage1HeroPrice()

    return {
      stack: [
        {
          image: propertyImage,
          width: 600,
          height: 300,
          margin: [0, 0, 0, 5],
        },
        {
          absolutePosition: { x: 150, y: 200 },
          canvas: [
            {
              type: 'rect',
              x: 0,
              y: 0,
              w: 300,
              h: 100,
              r: 2,
              color: 'black',
              fillOpacity: 0.3,
            },
          ],
        },
        {
          absolutePosition: { x: 0, y: 210 },
          stack: [
            {
              text: currencyFormat.format(offerPrice),
              fontSize: 28,
              color: 'white',
              bold: true,
              alignment: 'center',
            },
            {
              text: property.address,
              fontSize: 16,
              color: 'white',
              alignment: 'center',
            },
            {
              text:
                startCase(property.city) +
                ' ' +
                startCase(property.state) +
                ', ' +
                property.zip,
              fontSize: 16,
              color: 'white',
              alignment: 'center',
            },
          ],
        },
      ],
    }
  }

  async generatePage1LowerLeft() {
    const { property } = this
    const { images = [], proformaImages = [] } = property

    const whiteImage = await handleImageUrl('/images/white.jpg')

    let subImageOne = ''
    let subImageTwo = ''
    let mapImage = ''
    if (proformaImages.length <= 0)
      switch (images.length) {
        case 0: {
          subImageOne = await handleImageUrl('/images/default_image.png')
          subImageTwo = await handleImageUrl('/images/default_image.png')
          mapImage = await handleImageUrl('/images/default_image.png')
          break
        }
        case 1: {
          subImageOne = whiteImage
          subImageTwo = whiteImage
          mapImage = whiteImage
          break
        }
        case 2: {
          subImageOne = await handleImageUrl(images[1].url)
          subImageTwo = whiteImage
          mapImage = whiteImage
          break
        }
        case 3: {
          subImageOne = await handleImageUrl(images[1].url)
          subImageTwo = await handleImageUrl(images[2].url)
          mapImage = whiteImage
          break
        }
        default: {
          subImageOne = await handleImageUrl(images[1].url)
          subImageTwo = await handleImageUrl(images[2].url)
          mapImage = await handleImageUrl(images[3].url)
          break
        }
      }
    else
      switch (proformaImages.length) {
        case 1: {
          subImageOne = whiteImage
          subImageTwo = whiteImage
          mapImage = whiteImage
          break
        }
        case 2: {
          subImageOne = await handleImageUrl(proformaImages[1])
          subImageTwo = whiteImage
          mapImage = whiteImage
          break
        }
        case 3: {
          subImageOne = await handleImageUrl(proformaImages[1])
          subImageTwo = await handleImageUrl(proformaImages[2])
          mapImage = whiteImage
          break
        }
        default: {
          subImageOne = await handleImageUrl(proformaImages[1])
          subImageTwo = await handleImageUrl(proformaImages[2])
          mapImage = await handleImageUrl(proformaImages[3])
          break
        }
      }

    return {
      width: 215,
      stack: [
        {
          image: subImageOne,
          width: 200,
          height: 130,
          margin: 5,
        },
        {
          image: subImageTwo,
          width: 200,
          height: 130,
          margin: 5,
        },
        {
          image: mapImage,
          width: 200,
          height: 130,
          margin: 5,
        },
      ],
    }
  }

  async generateImagePages() {
    const { property } = this
    const { images = [] } = property
    const galleryImages = limitProformaGalleryImages(images)

    if (galleryImages.length === 0) return []

    const imagesPerPage = 15

    const imageUrls = compact(
      await Promise.all(
        galleryImages.map(async (image) => await handleImageUrl(image.url)),
      ),
    )

    if (imageUrls.length === 0) return []

    const pageBreakUrls = chunk(imageUrls, imagesPerPage)
      .map((pageUrls) => {
        const chunkedUrls = chunk(pageUrls, 3)
        return unzip(chunkedUrls)
      })
      .filter((pageImages) =>
        pageImages.some((columnImages) =>
          compact(columnImages).length > 0,
        ),
      )

    return pageBreakUrls.map((pageImages) => ({
      pageBreak: 'before',
      margin: [15, 10, 15, 10],
      stack: [
        {
          width: '*',
          stack: [
            {
              text: [
                'Property ',
                { text: 'Images', color: '#3399ff' },
              ],
              fontSize: 18,
              margin: [15, 12, 0, 0],
            },
            {
              canvas: [
                {
                  type: 'line',
                  x1: 15,
                  y1: 5,
                  x2: 150,
                  y2: 5,
                  lineWidth: 2,
                },
              ],
              margin: [0, 0, 0, 6],
            },
            {
              columns: map(pageImages, (columnImages) => ({
                width: 190,
                stack: map(compact(columnImages), (image) => {
                  return {
                    image: image,
                    width: 185,
                    height: 130,
                    margin: 5,
                  }
                }),
              })),
            },
          ],
        },
      ],
    }))
  }

  async generateFooter() {
    const { property } = this

    const { portfolio = {}, proformaContact } = property
    const { organization = {} } = portfolio

    const whiteImage = await handleImageUrl('/images/white.jpg')

    const orgEmail = proformaContact
      ? proformaContact.username
      : organization.email
        ? organization.email
        : 'Email Not Entered'
    const orgPhone = proformaContact
      ? formatPhoneNumber(proformaContact.phone)
      : organization.phone
        ? formatPhoneNumber(organization.phone)
        : 'Phone Not Entered'
    let orgLogo = property.proformaLogo
      ? await handleImageUrl(property.proformaLogo)
      : organization.logo
        ? organization.logo.url
          ? await handleImageUrl(organization.logo.url)
          : whiteImage
        : whiteImage

    if (!orgLogo) orgLogo = whiteImage

    // In-flow page-1 footer (not pdfmake footer callback / not absolutePosition).
    // Renders after the page-1 columns and before any pageBreak.
    return {
      unbreakable: true,
      margin: [5, 8, 5, 5],
      stack: [
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: 580,
              y2: 0,
              lineWidth: 2,
            },
          ],
          margin: [0, 0, 0, 4],
        },
        {
          columns: [
            {
              table: {
                headerRows: 1,
                widths: [70, 100, '*', 180],
                body: [
                  [
                    {
                      text: 'Powered By ',
                      alignment: 'left',
                      color: '#8e9194',
                      margin: [0, 10, 0, 0],
                    },
                    {
                      image: await handleImageUrl(
                        this.urlAffiliate
                          ? get(
                              this.affiliate,
                              'sidebar.sidebarMemberFullImage.0.url',
                            ) || '/images/NewLogoBlue.png'
                          : '/images/NewLogoBlue.png',
                      ),
                      width: 100,
                      height: 20,
                      margin: [-5, 7, 0, 0],
                    },
                    {
                      text: orgPhone,
                      alignment: 'right',
                      margin: [0, 10, 0, 0],
                    },
                    {
                      image: orgLogo,
                      fit: [125, 50],
                      margin: [0, 0, 0, 0],
                      alignment: 'center',
                      rowSpan: 2,
                    },
                  ],
                  [
                    {
                      text: 'Create Your Proforma at',
                      alignment: 'left',
                      margin: [0, -3, 0, 0],
                      color: '#8e9194',
                      fontSize: 8,
                      noWrap: true,
                    },
                    {
                      text: this.urlDomain || 'housefolios.com',
                      link:
                        'https://' +
                        (this.urlDomain || 'www.housefolios.com') +
                        '/',
                      alignment: 'left',
                      decoration: 'underline',
                      margin: [10, -3, 0, 0],
                      color: '#3399ff',
                      fontSize: 8,
                    },
                    {
                      text: orgEmail,
                      alignment: 'right',
                      margin: [0, -5, 0, 0],
                      fontSize:
                        orgEmail.length > 20
                          ? orgEmail.length > 30
                            ? 8
                            : 10
                          : 12,
                    },
                  ],
                ],
              },
              margin: [5, 0, 10, 0],
              layout: {
                defaultBorder: false,
              },
            },
          ],
        },
      ],
    }
  }
}

export default BasePDFGenerator
